import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { FALLBACK_CONFIG, retentionChoices, formatDuration, formatExpiry } from '../services/retention';
import { SecretCreateResponse, ServerConfig } from '../types';

type SecretType = 'text' | 'file';

const SecretCreator: React.FC = () => {
  const [secretType, setSecretType] = useState<SecretType>('text');
  const [content, setContent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [pin, setPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState('');
  const [created, setCreated] = useState<SecretCreateResponse | null>(null);
  const [config, setConfig] = useState<ServerConfig>(FALLBACK_CONFIG);
  const [expiresIn, setExpiresIn] = useState(FALLBACK_CONFIG.defaultExpiresIn);
  const [oneTime, setOneTime] = useState(FALLBACK_CONFIG.defaultOneTime);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Prefill from the password generator. The value arrives as router state,
  // which the browser keeps in session history, so drop it right after reading.
  const prefill = (location.state as { prefill?: string } | null)?.prefill;
  useEffect(() => {
    if (prefill === undefined) return;
    setSecretType('text');
    setContent(prefill);
    navigate(location.pathname, { replace: true, state: null });
  }, [prefill, navigate, location.pathname]);

  useEffect(() => {
    api.getConfig()
      .then(serverConfig => {
        setConfig(serverConfig);
        setExpiresIn(serverConfig.defaultExpiresIn);
        setOneTime(serverConfig.defaultOneTime);
      })
      .catch(() => { /* keep the fallback; the server still validates */ });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Check file size (e.g., 10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (selectedFile.size > maxSize) {
        setError('Datei ist zu groß. Maximale Größe: 10MB');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (secretType === 'text' && !content) {
      setError('Bitte geben Sie einen Text ein');
      return;
    }
    
    if (secretType === 'file' && !file) {
      setError('Bitte wählen Sie eine Datei aus');
      return;
    }
    
    if (!pin) {
      setError('Bitte geben Sie eine PIN ein');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const secretData = secretType === 'text' ? content : file!;
      const response = await api.createSecret(secretData, pin, { expiresIn, oneTime });
      setCreated(response);
      setGeneratedLink(response.url);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Erstellen des Geheimnisses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetForm = () => {
    setGeneratedLink('');
    setCreated(null);
    setContent('');
    setFile(null);
    setPin('');
    setError(null);
  };

  if (generatedLink) {
    return (
      <div className="glass-panel p-8 rounded-2xl shadow-2xl animate-in fade-in zoom-in duration-300">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-white">Geheimnis erstellt!</h2>
          <p className="text-slate-400 mt-2">Teilen Sie diesen Link und Ihre PIN mit dem Empfänger.</p>
        </div>
        <div className="space-y-4">
          <div className="relative">
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">geheimer Link</label>
            <div className="flex gap-2">
              <input
                readOnly
                value={generatedLink}
                className="flex-1 bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-3 text-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-mono truncate"
              />
              <button
                onClick={handleCopy}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  copied 
                  ? 'bg-green-600 text-white' 
                  : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                }`}
              >
                {copied ? 'Kopiert!' : 'kopieren'}
              </button>
            </div>
          </div>
          <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <p className="text-amber-200 text-sm flex gap-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>Vergessen Sie die PIN nicht! Der Empfänger benötigt die PIN, um das Geheimnis anzuzeigen.</span>
            </p>
          </div>
          {created && (
            <p className="text-slate-400 text-sm text-center">
              {created.oneTime ? 'Kann einmal angezeigt werden, ' : 'Kann beliebig oft angezeigt werden, '}
              gültig bis {formatExpiry(created.expiresAt)}.
            </p>
          )}
          <button
            onClick={resetForm}
            className="w-full py-3 text-slate-400 hover:text-white transition-colors text-sm font-medium"
          >
            Noch ein Geheimnis erstellen
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="glass-panel p-8 rounded-2xl shadow-2xl space-y-6">
      {/* Tab Selector */}
      <div className="flex gap-2 p-1 bg-slate-900/50 rounded-xl border border-slate-700">
        <button
          type="button"
          onClick={() => setSecretType('text')}
          className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
            secretType === 'text'
              ? 'bg-indigo-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Text
          </div>
        </button>
        <button
          type="button"
          onClick={() => setSecretType('file')}
          className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all ${
            secretType === 'file'
              ? 'bg-indigo-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Datei
          </div>
        </button>
      </div>

      {/* Content Area */}
      {secretType === 'text' ? (
        <div className="space-y-2">
          <label htmlFor="content" className="block text-sm font-medium text-slate-300">
            Geheimer Text
          </label>
          <textarea
            id="content"
            required
            rows={6}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Fügen Sie Ihr Geheimnis hier ein..."
            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
          />
        </div>
      ) : (
        <div className="space-y-2">
          <label htmlFor="file" className="block text-sm font-medium text-slate-300">
            Datei auswählen
          </label>
          <div className="relative">
            <input
              type="file"
              id="file"
              onChange={handleFileChange}
              className="hidden"
            />
            <label
              htmlFor="file"
              className="flex flex-col items-center justify-center w-full h-40 bg-slate-900/50 border-2 border-dashed border-slate-700 rounded-xl cursor-pointer hover:border-indigo-500 hover:bg-slate-900/70 transition-all"
            >
              {file ? (
                <div className="text-center">
                  <svg className="w-12 h-12 text-indigo-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-slate-400 text-sm mt-1">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <p className="text-indigo-400 text-xs mt-2">Klicken um zu ändern</p>
                </div>
              ) : (
                <div className="text-center">
                  <svg className="w-12 h-12 text-slate-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-slate-400">
                    <span className="text-indigo-400 font-medium">Klicken Sie hier</span> um eine Datei auszuwählen
                  </p>
                  <p className="text-slate-600 text-xs mt-1">Max. 10MB</p>
                </div>
              )}
            </label>
          </div>
        </div>
      )}

      {/* PIN Input */}
      <div className="space-y-2">
        <label htmlFor="pin" className="block text-sm font-medium text-slate-300">
          PIN
        </label>
        <div className="relative">
          <input
            id="pin"
            type="password"
            required
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Vergeben Sie eine PIN"
            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 pl-11 text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-1">Empfänger benötigen diese PIN, um das Geheimnis anzuzeigen.</p>
      </div>

      {/* Lifetime */}
      <div className="space-y-3">
        <div className="space-y-2">
          <label htmlFor="expires-in" className="block text-sm font-medium text-slate-300">
            Gültigkeit
          </label>
          <select
            id="expires-in"
            value={expiresIn}
            onChange={(e) => setExpiresIn(Number(e.target.value))}
            className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {retentionChoices(config).map(seconds => (
              <option key={seconds} value={seconds}>{formatDuration(seconds)}</option>
            ))}
          </select>
        </div>
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={oneTime}
            onChange={(e) => setOneTime(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 accent-indigo-500"
          />
          <span>
            <span className="text-sm text-slate-300">Nach dem ersten Anzeigen löschen</span>
            <span className="block text-xs text-slate-500">Der Link funktioniert nur einmal. Sonst bleibt das Geheimnis bis zum Ablauf abrufbar.</span>
          </span>
        </label>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-600/50 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-xl shadow-lg shadow-indigo-500/20 transition-all active:scale-[0.98]"
      >
        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {secretType === 'file' ? 'Wird hochgeladen...' : 'Generiere...'}
          </div>
        ) : 'Link erstellen'}
      </button>
    </form>
  );
};

export default SecretCreator;